import { useState, useEffect, useCallback } from 'react';

interface CountdownState {
  hours: number;
  minutes: number;
  seconds: number;
  isExpired: boolean;
  formatted: string;
}

export const useCountdown = (targetDate: Date | string | null): CountdownState => {
  const calculateTimeLeft = useCallback((): CountdownState => {
    if (!targetDate) {
      return { hours: 0, minutes: 0, seconds: 0, isExpired: true, formatted: '00:00:00' };
    }

    const target = typeof targetDate === 'string' ? new Date(targetDate) : targetDate;
    const now = new Date();
    const difference = target.getTime() - now.getTime();

    if (difference <= 0) {
      return { hours: 0, minutes: 0, seconds: 0, isExpired: true, formatted: '00:00:00' };
    }

    const hours = Math.floor(difference / (1000 * 60 * 60));
    const minutes = Math.floor((difference % (1000 * 60 * 60)) / (1000 * 60));
    const seconds = Math.floor((difference % (1000 * 60)) / 1000);

    const formatted = [
      hours.toString().padStart(2, '0'),
      minutes.toString().padStart(2, '0'),
      seconds.toString().padStart(2, '0')
    ].join(':');

    return { hours, minutes, seconds, isExpired: false, formatted };
  }, [targetDate]);

  const [timeLeft, setTimeLeft] = useState<CountdownState>(calculateTimeLeft);

  useEffect(() => {
    // Recalcular imediatamente quando targetDate mudar
    setTimeLeft(calculateTimeLeft());

    if (!targetDate) return;

    const interval = setInterval(() => {
      const newTimeLeft = calculateTimeLeft();
      setTimeLeft(newTimeLeft);

      // Parar o intervalo quando expirar
      if (newTimeLeft.isExpired) {
        clearInterval(interval);
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [targetDate, calculateTimeLeft]);

  return timeLeft;
};
